import { useEffect, useState } from "react";
import { FileBarChart, Sparkles, Trash2, Copy, Check } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet",
  "août", "septembre", "octobre", "novembre", "décembre"];
const monthLabel = (m) => { const [y, mm] = m.split("-"); return `${MONTHS_FR[Number(mm) - 1]} ${y}`; };
const fmtMin = (min) => `${Math.floor((min || 0) / 60)} h ${String((min || 0) % 60).padStart(2, "0")}`;

export default function ReportsTab({ clientId, clientName }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [reports, setReports] = useState([]);
  const [current, setCurrent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = () => api.get(`/clients/${clientId}/reports`).then((r) => setReports(r.data)).catch(() => {});
  useEffect(() => { load(); }, [clientId]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/clients/${clientId}/reports/generate`, { month });
      setCurrent(data);
      toast.success("Rapport généré avec analyse IA");
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce rapport ?")) return;
    await api.delete(`/reports/${id}`);
    if (current?.id === id) setCurrent(null);
    toast.success("Rapport supprimé");
    load();
  };

  const copyReport = async () => {
    if (!current) return;
    const s = current.stats;
    const text = [
      `Rapport mensuel — ${current.client_name} — ${monthLabel(current.month)}`,
      "",
      `Publications : ${s.pubs_total} planifiées · ${s.pubs_published} publiées · ${s.pubs_pending} en attente`,
      `Temps de travail : ${fmtMin(s.time_minutes)}`,
      `Demandes ponctuelles : ${s.requests_total} (dont ${s.requests_done} traitées)`,
      "",
      "Analyse et recommandations :",
      current.insights,
    ].join("\n");
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Rapport copié — prêt à être envoyé au client");
  };

  return (
    <div className="space-y-5 max-w-3xl" data-testid="reports-tab">
      <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5 flex flex-wrap items-end gap-3">
        <div>
          <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-1">Mois du rapport</p>
          <Input type="month" data-testid="report-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" />
        </div>
        <Button data-testid="report-generate" onClick={generate} disabled={loading}
          className="bg-neutral-900 hover:bg-neutral-800 text-white gap-1.5">
          <Sparkles className="w-4 h-4" /> {loading ? "Génération en cours (IA)…" : "Générer le rapport"}
        </Button>
        {current && (
          <Button variant="outline" data-testid="report-copy" onClick={copyReport} className="gap-1.5">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />} Copier pour le client
          </Button>
        )}
      </div>

      {current && (
        <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-6 space-y-5 animate-fade-up" data-testid="report-result">
          <div>
            <h3 className="font-heading font-bold text-lg">Rapport de {monthLabel(current.month)} — {current.client_name}</h3>
            <p className="text-xs text-neutral-400 mt-0.5">Généré le {new Date(current.created_at).toLocaleDateString("fr-FR")} par {current.created_by}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["Publications planifiées", current.stats.pubs_total],
              ["Publiées", current.stats.pubs_published],
              ["En attente de validation", current.stats.pubs_pending],
              ["Temps de travail", fmtMin(current.stats.time_minutes)],
            ].map(([l, v]) => (
              <div key={l} className="bg-neutral-50 border border-neutral-100 rounded-lg p-3">
                <p className="font-heading text-xl font-bold">{v}</p>
                <p className="text-[11px] text-neutral-500 mt-0.5">{l}</p>
              </div>
            ))}
          </div>
          {(current.stats.requests_total > 0 || current.stats.content_requests_total > 0) && (
            <p className="text-xs text-neutral-500">
              Demandes ponctuelles : {current.stats.requests_total} (dont {current.stats.requests_done} traitées)
              {current.stats.content_requests_total > 0 && ` · Contenus demandés au client : ${current.stats.content_requests_total} (dont ${current.stats.content_requests_done} reçus)`}
            </p>
          )}
          <div>
            <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Analyse et recommandations (IA)</p>
            <p className="text-sm text-neutral-700 whitespace-pre-line leading-relaxed" data-testid="report-insights">{current.insights}</p>
          </div>
          {current.stats.pub_titles?.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Publications du mois</p>
              <div className="space-y-1">
                {current.stats.pub_titles.map((t, i) => (
                  <p key={i} className="text-xs text-neutral-600">· {t.title} <span className="text-neutral-400">({t.date?.slice(8, 10)}/{t.date?.slice(5, 7)})</span></p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {reports.length > 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl shadow-sm overflow-hidden" data-testid="report-history">
          <p className="px-5 py-3.5 border-b border-neutral-100 font-heading font-bold text-sm">Rapports précédents</p>
          {reports.map((r) => (
            <div key={r.id} className="flex items-center gap-3 px-5 py-3 border-b border-neutral-100 last:border-0" data-testid={`report-item-${r.id}`}>
              <FileBarChart className="w-4 h-4 text-neutral-400 shrink-0" />
              <button className="flex-1 text-left text-sm font-medium hover:underline" data-testid={`report-open-${r.id}`}
                onClick={() => setCurrent(r)}>
                Rapport de {monthLabel(r.month)}
              </button>
              <span className="text-[11px] text-neutral-400">{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
              <button data-testid={`report-delete-${r.id}`} onClick={() => remove(r.id)}
                className="text-neutral-300 hover:text-red-500 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {!current && reports.length === 0 && (
        <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-12 text-center" data-testid="reports-empty">
          <FileBarChart className="w-8 h-8 mx-auto text-neutral-300 mb-3" />
          <p className="font-heading font-bold">Aucun rapport pour {clientName}</p>
          <p className="text-sm text-neutral-500 mt-1">Choisissez un mois et générez le premier rapport avec analyse IA.</p>
        </div>
      )}
    </div>
  );
}
